import type { EngForm } from "@server/submissions/schema";
import type { TicketType } from "@server/submissions/types";
import type { StoredTriageResult } from "@server/triage/schema";

export const SUBMISSION_STATUSES = [
  "TRIAGING",
  "PASSED",
  "FLAGGED",
  "TICKET_QUEUED",
  "TICKET_CREATED",
  "TICKET_FAILED",
  "CLOSED_NO_TICKET",
] as const;
export type SubmissionStatus = (typeof SUBMISSION_STATUSES)[number];

export interface Me {
  email: string | null;
  name: string | null;
  image: string | null;
  isAdmin: boolean;
  // Name of the Slack channel tickets are posted to, without the #.
  ticketsChannel: string;
}

// Mirrors SubmissionView in src/submissions/service.ts, which pulls in Node-only code.
export interface SubmissionView {
  id: string;
  kind: "eng";
  status: SubmissionStatus;
  createdAt: string;
  statusChangedAt: string;
  creatorEmail: string;
  creatorName: string;
  form: EngForm;
  // What the ticket will be raised as, and what triage said it was (absent if triage was unavailable).
  ticketType: TicketType;
  aiTicketType?: TicketType;
  ticketTypeCorrected: boolean;
  triage?: { n: number; at: string; failedOpen: boolean; reason?: string; result?: StoredTriageResult };
  // What the AI is checking right now, while the submission is being triaged.
  progress?: { steps: { label: string; count: number }[] };
  acknowledged: boolean;
  override?: { by: string; at: string; reason: string };
  ticket?: TicketView;
}

// The part of the ticket checkpoint record the screens need.
export interface TicketView {
  lastError?: string;
  title?: string;
  steps?: {
    listItem?: { value?: { id: string; url: string } };
    jira?: { value?: { key: string; url: string; assigned: boolean } };
  };
}

export interface SubmissionSummary {
  id: string;
  status: SubmissionStatus;
  createdAt: string;
  statusChangedAt: string;
  creatorEmail: string;
  ticketType: TicketType;
  ticketTypeCorrected: boolean;
  companyName: string;
  severity: EngForm["severity"];
  overridden: boolean;
}

// Statuses the server is still working on; the screen polls until it leaves them.
export const WORKING_STATUSES: readonly SubmissionStatus[] = ["TRIAGING", "TICKET_QUEUED"];
